import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";

/** Единый разбор ошибки API. Отдельного извлекателя у скан-экранов нет: их
 *  было два, и каждый не понимал того, что понимал другой (массив 422 против
 *  вложенного `detail`) — на экране это выглядело как «Request failed with
 *  status code 422» вместо текста проверки. */
export const apiErrorText = extractErrorMessage;

/**
 * Состояние загрузки экрана: спиннер ИЛИ ошибка с кнопкой «Повторить».
 *
 * Отдельный компонент, потому что `isLoading || !data` — ловушка: в
 * react-query v5 при ошибке `isLoading=false`, а `data=undefined`, и такой
 * экран крутит спиннер вечно. На иммерсивном роуте это тупик — шапки и меню
 * нет, выйти некуда. Поэтому шапку со стрелкой «назад» рендерим ВСЕГДА, а
 * этот блок кладём только в тело экрана.
 */
export function LoadState({
  isLoading,
  error,
  onRetry,
  className = "flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center",
}: {
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  className?: string;
}): React.ReactElement {
  if (isLoading) {
    return (
      <div className={className}>
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className={className}>
      <AlertTriangle className="size-10 text-rose-600" />
      <p className="text-sm text-rose-700">{apiErrorText(error, "Не удалось загрузить данные")}</p>
      <Button variant="outline" onClick={onRetry}>
        Повторить
      </Button>
    </div>
  );
}

/** Отказ по правам/привязке (403). Отдельно от «не удалось загрузить»:
 *  причину такого отказа сервер называет словами, и её надо показать. */
export function isForbidden(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 403;
}

/**
 * Плашка «доступ закрыт»: текст — `detail` сервера, кнопки «Повторить» нет.
 *
 * ⚠️ Отдельно от `LoadState` намеренно. «Вы не привязаны к точке сборки» —
 * не сбой связи: повтор вернёт ровно тот же ответ, пока админ не привяжет
 * человека, и кнопка «Повторить» предлагала бы чинить это тапом.
 */
export function AccessDenied({
  error,
  className = "flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center",
}: {
  error: unknown;
  className?: string;
}): React.ReactElement {
  return (
    <div className={className}>
      <ShieldAlert className="size-10 text-amber-600" />
      <p className="text-sm font-semibold text-amber-800">
        {apiErrorText(error, "Доступ к разделу закрыт")}
      </p>
      <p className="text-xs text-muted-foreground">
        Это настройка доступа, а не сбой связи — обратитесь к администратору портала.
      </p>
    </div>
  );
}

/**
 * Возврат «назад» без раздувания истории.
 *
 * `navigate("/fbs")` — это push: экран сбора остаётся в стеке, и аппаратная
 * «назад» кидает кладовщика обратно в скан. Поэтому если в истории есть куда
 * возвращаться (react-router держит индекс записи в `history.state.idx`), идём
 * на шаг назад; если экран открыт по прямой ссылке — заменяем запись fallback'ом.
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback]);
}
